import { AppState, ConfigurationContext } from '@news-mono/web-common'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAuthActions } from '../../user-registration/authentication/AuthenticationProvider'
import { StyledUnsubscribe } from '../components/Unsubscribe.styled'
import { UnsubscribeBody } from '../components/UnsubscribeBody'
import { UnsubscribeFooter } from '../components/UnsubscribeFooter'
import { UnsubscribeHeader } from '../components/UnsubscribeHeader'
import { EPC_STORAGE_KEY } from '../EmailPreference'
import {
    authClickHandler,
    useGetEmailToken,
} from '../hooks/useEmailPreferenceData'
import {
    getQuery,
    getStorage,
    useResolveEmail,
    useValidateIdAndToken,
} from '../hooks/useUnsubscribe'

export type UnsubscribeStatus = 'initial' | 'error' | 'success'
export interface UnsubscribeParam {
    listId?: string
    unsubId?: string
    tokens: {
        urlToken?: string
        profileToken?: string
    }
}

export const Unsubscribe = () => {
    const { emailServiceApi } = useContext(ConfigurationContext)
    const paramRef = useRef<UnsubscribeParam>()
    const { onLoginClick, onRegisterClick } = useAuthActions()
    const { userEmail, isLoggedIn } = useSelector(
        (state: AppState) => state.authentication,
    )
    const [unsubscribeStatus, setUnsubscribeStatus] =
        useState<UnsubscribeStatus>('initial')
    const [useProfileEmail, setUseProfileEmail] = useState<boolean>(false)
    const storageData = getStorage(EPC_STORAGE_KEY) as {
        useProfileEmail: boolean
    }

    useEffect(() => {
        setUseProfileEmail(
            !!storageData && storageData.useProfileEmail && isLoggedIn,
        )

        if (!storageData || !isLoggedIn) {
            paramRef.current = getQuery()
            if (
                (!paramRef.current.listId && !paramRef.current.unsubId) ||
                !paramRef.current.tokens.urlToken
            ) {
                throw new Error('Invalid link')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn])

    const { data: tokenData, error: tokenError } = useGetEmailToken({
        enabled: isLoggedIn,
    })

    // If logged-in and fetched a token, store profile token
    if (tokenData?.token) {
        paramRef.current = {
            listId: paramRef.current?.listId,
            unsubId: paramRef.current?.unsubId,
            tokens: {
                ...paramRef.current?.tokens,
                profileToken: tokenData?.token,
            },
        }
    }

    const { subscribeAccount, error: validateListIdError } =
        useValidateIdAndToken(paramRef.current)

    const { resolveEmailError, data: emailsData } = useResolveEmail(
        useProfileEmail,
        emailServiceApi,
        paramRef.current?.tokens.urlToken,
        userEmail,
    )

    const pageError = validateListIdError || tokenError
    if (pageError) {
        throw new Error(pageError as string)
    }
    if (resolveEmailError) {
        throw resolveEmailError
    }

    function updateStatus(status: UnsubscribeStatus) {
        setUnsubscribeStatus(status)
    }

    function onSwitchEmailClick(e: React.MouseEvent<HTMLElement>) {
        e.preventDefault()
        setUseProfileEmail((useProfileEmail) => !useProfileEmail)
        sessionStorage.setItem(
            EPC_STORAGE_KEY,
            JSON.stringify({
                useProfileEmail: !useProfileEmail,
            }),
        )
    }

    return (
        <StyledUnsubscribe>
            <UnsubscribeHeader
                isLoggedIn={isLoggedIn}
                emails={emailsData}
                status={unsubscribeStatus}
                useProfileEmail={useProfileEmail}
                onLoginClick={(e) =>
                    authClickHandler(e, EPC_STORAGE_KEY, onLoginClick)
                }
                onRegisterClick={(e) =>
                    authClickHandler(e, EPC_STORAGE_KEY, onRegisterClick)
                }
                onSwitchEmailClick={(e) => onSwitchEmailClick(e)}
            />

            <UnsubscribeBody
                status={unsubscribeStatus}
                subscribeAccount={subscribeAccount}
                useProfileEmail={useProfileEmail}
                tokens={paramRef.current?.tokens}
                updateStatus={(status) => updateStatus(status)}
            />
            <UnsubscribeFooter />
        </StyledUnsubscribe>
    )
}
